import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  from,
  Observable,
  split,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';

export interface ApolloClientOptions {
  getToken: () => Promise<string | null>;
  graphqlUrl: string;
  /** Pre-hydrated cache (e.g. restored from IndexedDB). Falls back to empty InMemoryCache. */
  cache?: InMemoryCache;
}

export function createApolloClient(
  getTokenOrOpts: (() => Promise<string | null>) | ApolloClientOptions,
  graphqlUrl?: string,
) {
  // Support both old signature (getToken, url) and new options object
  const opts: ApolloClientOptions =
    typeof getTokenOrOpts === 'function'
      ? { getToken: getTokenOrOpts, graphqlUrl: graphqlUrl ?? '' }
      : getTokenOrOpts;

  const httpLink = new HttpLink({
    uri: opts.graphqlUrl,
  });

  const authLink = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      // Check if authorization header already provided in context
      // This allows callers to bypass the token fetch (e.g., signup before setAuth)
      const existingHeaders = operation.getContext().headers;
      if (existingHeaders?.authorization) {
        forward(operation).subscribe(observer);
        return;
      }

      opts.getToken().then((token) => {
        // Auto-detect share-link context from URL path (/share/folder/:id or /share/playlist/:id).
        // Sends object-id header so backend GqlAuthGuard can augment
        // real-user auth with share-link team context (avoids INVALID_TEAM for cross-team shares).
        // IMPORTANT: This header triggers identity replacement on the backend (accountId, isGuestUser,
        // operation allowlist). It MUST only be sent on actual share-link pages (/share/*).
        // The URL regex below guarantees this — do NOT send this header from other pages.
        const shareLinkHeaders: Record<string, string> = {};
        const pathMatch =
          typeof window !== 'undefined'
            ? window.location.pathname.match(/^\/share\/(?:folder|playlist)\/([^/]+)/)
            : null;
        if (pathMatch?.[1]) {
          shareLinkHeaders['object-id'] = pathMatch[1];
        }

        operation.setContext((prevContext: Record<string, unknown>) => ({
          headers: {
            ...(prevContext.headers as Record<string, string> | undefined),
            ...shareLinkHeaders,
            authorization: token ? `Bearer ${token}` : '',
          },
        }));
        forward(operation).subscribe(observer);
      });
    });
  });

  const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      for (const err of error.errors) {
        if (
          (err.extensions?.code === 'UNAUTHENTICATED' &&
            err.message !== 'INVALID_TEAM' &&
            err.message !== 'NOT_FOUND_USER') ||
          err.message === 'API_TOKEN_EXPIRED' ||
          err.message === 'API_TOKEN_INVALID'
        ) {
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
        if (err.message === 'INACTIVE_ACCOUNT') {
          window.dispatchEvent(new CustomEvent('auth:account-inactive'));
        }
      }
    } else {
      console.error('[Network error]:', error);
    }
  });

  // Build terminal link: split subscriptions to WS, queries/mutations to HTTP
  // Derive WS URL from HTTP URL: https → wss, http → ws
  const wsUrl = opts.graphqlUrl.replace(/^http/, 'ws');
  const wsLink = new GraphQLWsLink(
    createClient({
      url: wsUrl,
      lazy: true,
      retryAttempts: Infinity,
      shouldRetry: () => true,
      connectionParams: async () => {
        const token = await opts.getToken();
        if (!token) throw new Error('No auth token — retry after Firebase initializes');
        return { authorization: `Bearer ${token}` };
      },
    }),
  );

  const terminalLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink,
  );

  return new ApolloClient({
    link: from([errorLink, authLink, terminalLink]),
    cache: opts.cache ?? new InMemoryCache(),
  });
}
